import { BUFFER_HEIGHT, FROG_X, GRAVITY, HEAD_H, JUMP_V0, TICK_HZ, TONGUE_REACH } from './constants';
import type { Sim } from './sim';

const RISE_TICKS = Math.floor((JUMP_V0 / GRAVITY) * TICK_HZ);

type Probe = { kind: 'early' } | { kind: 'late' } | { kind: 'ok'; y: number };

/**
 * Looks ahead on a copy of the simulation: wait `delay` ticks, tap, and report
 * how high the frog is when the rope next passes under it.
 */
function probe(sim: Sim, delay: number, horizon = 260): Probe {
  const c = sim.clone();
  c.bubble = false;
  for (let i = 0; i < horizon && !c.over; i++) {
    if (i === delay) c.input(1);
    if (i === delay + 1) c.input(0);
    c.step();
    for (const e of c.events) {
      if (e.t === 'cross') {
        if (i < delay) return { kind: 'late' };
        return { kind: 'ok', y: e.y };
      }
      if (e.t === 'tangle') {
        // Still rising (first half of a tap jump) means we pressed too late;
        // coming down or already landed means too early.
        if (i < delay || i - delay <= RISE_TICKS) return { kind: 'late' };
        return { kind: 'early' };
      }
    }
    c.events.length = 0;
  }
  return { kind: 'early' };
}

/** True when tapping now gives the highest (safest) clearance over the rope. */
export function shouldJump(sim: Sim): boolean {
  if (sim.air || sim.over || sim.stunUntil > sim.tick) return false;
  const now = probe(sim, 0);
  if (now.kind === 'early') return false;
  if (now.kind === 'late') return true;
  const next = probe(sim, 1);
  if (next.kind !== 'ok') return true;
  return now.y >= next.y;
}

/** True when a tongue flick right now would catch something. */
export function shouldLick(sim: Sim): boolean {
  if (!sim.air || sim.tongueUntil > sim.tick) return false;
  if (sim.vy < 0 && sim.y < BUFFER_HEIGHT) return false;
  const headH = sim.y + HEAD_H;
  return sim.targets.some((t) => {
    const dx = t.x - FROG_X;
    const dh = t.h - headH;
    return dx * dx + dh * dh <= TONGUE_REACH * TONGUE_REACH * 0.8;
  });
}

/** Drives a simulation with taps, for the attract-mode demo and for tests. */
export class Autopilot {
  private releaseAt = -1;
  constructor(private lick = true) {}

  /** Call once per tick, before sim.step(). */
  act(sim: Sim): void {
    if (this.releaseAt === sim.tick) {
      sim.input(0);
      this.releaseAt = -1;
    }
    if (this.releaseAt >= 0) return;
    if (shouldJump(sim) || (this.lick && shouldLick(sim))) {
      sim.input(1);
      this.releaseAt = sim.tick + 1;
    }
  }
}
